import { api } from "../../../api/Middleware";
import {
  SELLER_STORES_URL,
  SELLER_STORES_LIST_URL,
  SELLER_STORES_CREATE_URL,
  SELLER_STORES_DETAIL_URL,
  SELLER_STORES_UPDATE_URL,
  SELLER_STORES_DELETE_URL,
  SELLER_STORES_CHECK_NAME_URL,
  SELLER_STORES_CHECK_SLUG_AVAILABILITY_URL,
} from "../../../api/Urls";

// =============================
// SELLER STORE SERVICES
// =============================

class SellerStoreServices {
  // =============================
  // CRUD OPERATIONS
  // =============================

  // Listar lojas da organization do usuário
  static async listStores(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${SELLER_STORES_LIST_URL}?${queryString}` : SELLER_STORES_LIST_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar lojas do seller:', error);
      throw error;
    }
  }

  // Buscar loja específica
  static async getStore(storeSlug) {
    try {
      return await api.get(SELLER_STORES_DETAIL_URL(storeSlug));
    } catch (error) {
      console.error('Erro ao buscar loja do seller:', error);
      throw error;
    }
  }

  // Criar nova loja
  static async createStore(data) {
    try {
      return await api.post(SELLER_STORES_CREATE_URL, data);
    } catch (error) {
      console.error('Erro ao criar loja do seller:', error);
      throw error;
    }
  }

  // Criar nova loja com arquivo (FormData)
  static async createStoreWithFile(formData) {
    try {
      // Não definir Content-Type manualmente - deixar o navegador definir com boundary
      return await api.post(SELLER_STORES_CREATE_URL, formData);
    } catch (error) {
      console.error('Erro ao criar loja do seller com arquivo:', error);
      throw error;
    }
  }

  // Atualizar loja existente (PUT)
  static async updateStore(storeSlug, data) {
    try {
      return await api.put(SELLER_STORES_UPDATE_URL(storeSlug), data);
    } catch (error) {
      console.error('Erro ao atualizar loja do seller:', error);
      throw error;
    }
  }

  // Atualizar loja existente com arquivo (PUT + FormData)
  static async updateStoreWithFile(storeSlug, formData) {
    try {
      // Não definir Content-Type manualmente - deixar o navegador definir com boundary
      return await api.put(SELLER_STORES_UPDATE_URL(storeSlug), formData);
    } catch (error) {
      console.error('Erro ao atualizar loja do seller com arquivo:', error);
      throw error;
    }
  }

  // Atualizar parcialmente loja (PATCH)
  static async patchStore(storeSlug, data) {
    try {
      return await api.patch(SELLER_STORES_UPDATE_URL(storeSlug), data);
    } catch (error) {
      console.error('Erro ao atualizar loja do seller:', error);
      throw error;
    }
  }

  // Atualizar parcialmente loja com arquivo (PATCH + FormData)
  static async patchStoreWithFile(storeSlug, formData) {
    try {
      // Não definir Content-Type manualmente - deixar o navegador definir com boundary
      return await api.patch(SELLER_STORES_UPDATE_URL(storeSlug), formData);
    } catch (error) {
      console.error('Erro ao atualizar loja do seller com arquivo:', error);
      throw error;
    }
  }

  // Desativar loja (soft delete)
  static async deactivateStore(storeSlug) {
    try {
      return await api.delete(SELLER_STORES_DELETE_URL(storeSlug));
    } catch (error) {
      console.error('Erro ao desativar loja do seller:', error);
      throw error;
    }
  }

  // Verificar se nome da loja está disponível
  static async checkStoreName(name) {
    try {
      const response = await api.get(`${SELLER_STORES_CHECK_NAME_URL}?name=${encodeURIComponent(name)}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar nome da loja:', error);
      throw error;
    }
  }

  // Verificar disponibilidade do slug da loja
  static async checkStoreSlugAvailability(slug) {
    try {
      const response = await api.get(`${SELLER_STORES_CHECK_SLUG_AVAILABILITY_URL}?slug=${encodeURIComponent(slug)}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao verificar disponibilidade do slug:', error);
      throw error;
    }
  }

  // =============================
  // VALIDATION HELPERS
  // =============================

  // Validar dados da loja
  static validateStoreData(data) {
    const errors = {};

    // Validar email
    if (data.contact_info?.email && !SellerStoreServices.isValidEmail(data.contact_info.email)) {
      errors['contact_info.email'] = 'Email inválido';
    }

    // Validar URLs das redes sociais
    const socialLinksFields = ['website', 'facebook', 'instagram', 'linkedin', 'twitter'];
    socialLinksFields.forEach(field => {
      const url = data.social_links?.[field];
      if (url && !SellerStoreServices.isValidUrl(url)) {
        errors[`social_links.${field}`] = 'URL inválida';
      }
    });

    // Validar Meta Pixel ID
    if (data.meta_pixel_id && !SellerStoreServices.isValidMetaPixelId(data.meta_pixel_id)) {
      errors['meta_pixel_id'] = 'Meta Pixel ID inválido (deve conter apenas números)';
    }

    // Validar Google Analytics ID
    if (data.google_analytics_id && !SellerStoreServices.isValidGoogleAnalyticsId(data.google_analytics_id)) {
      errors['google_analytics_id'] = 'Google Analytics ID inválido (formato: GA-XXXXXXXXX-X ou G-XXXXXXXXXX)';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Validar email
  static isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Validar URL
  static isValidUrl(string) {
    try {
      new URL(string.startsWith('http') ? string : `https://${string}`);
      return true;
    } catch (_) {
      return false;
    }
  }

  // Validar Meta Pixel ID (apenas números, 15-16 dígitos)
  static isValidMetaPixelId(pixelId) {
    const pixelRegex = /^\d{15,16}$/;
    return pixelRegex.test(pixelId.trim());
  }

  // Validar Google Analytics ID (GA-XXXXXXXXX-X ou G-XXXXXXXXXX)
  static isValidGoogleAnalyticsId(gaId) {
    const gaRegex = /^(GA-\d{9}-\d|G-[A-Z0-9]{10})$/;
    return gaRegex.test(gaId.trim().toUpperCase());
  }

  // =============================
  // FILE UPLOAD HELPERS
  // =============================

  // Validar arquivo de imagem
  static validateImageFile(file, maxSizeMB = 5) {
    const errors = [];
    
    if (!file) {
      errors.push("Arquivo é obrigatório");
      return { isValid: false, errors };
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      errors.push("Formato de arquivo inválido. Apenas JPG, PNG e WebP são aceitos.");
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Converter arquivo para base64
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  // =============================
  // DATA FORMATTING HELPERS
  // =============================

  // Preparar dados para envio à API
  static async prepareStoreData(formData) {
    const data = {
      social_links: formData.social_links || {},
      contact_info: formData.contact_info || {},
      meta_pixel_id: formData.meta_pixel_id || "",
      google_analytics_id: formData.google_analytics_id || "",
      is_active: formData.is_active !== undefined ? formData.is_active : true
    };

    // Se há uma nova imagem, converter para base64
    if (formData.banner_image instanceof File) {
      try {
        data.banner_image = await SellerStoreServices.fileToBase64(formData.banner_image);
      } catch (error) {
        throw new Error('Erro ao processar imagem: ' + error.message);
      }
    }

    return data;
  }

  // Formatar dados da loja para exibição
  static formatStoreData(store) {
    if (!store) return null;

    // Garantir que objetos aninhados existem
    const safeStore = {
      ...store,
      social_links: store.social_links || {},
      contact_info: store.contact_info || {},
      meta_pixel_id: store.meta_pixel_id || "",
      google_analytics_id: store.google_analytics_id || "",
      owner: store.owner || {},
      badges: store.badges || [],
      total_agents: store.total_agents || 0,
      total_sales: store.total_sales || 0,
      average_rating: store.average_rating || 0.0,
      is_active: store.is_active !== undefined ? store.is_active : true
    };

    return {
      ...safeStore,
      formattedCreatedAt: safeStore.created_at 
        ? new Date(safeStore.created_at).toLocaleDateString('pt-BR')
        : '',
      formattedUpdatedAt: safeStore.updated_at
        ? new Date(safeStore.updated_at).toLocaleDateString('pt-BR')
        : '',
      hasContactInfo: !!(safeStore.contact_info.email || safeStore.contact_info.phone),
      hasSocialLinks: !!(
        safeStore.social_links.website || 
        safeStore.social_links.facebook || 
        safeStore.social_links.instagram || 
        safeStore.social_links.linkedin || 
        safeStore.social_links.twitter
      ),
      badgeCount: safeStore.badges.length,
      statusText: safeStore.is_active ? 'Ativa' : 'Inativa',
      statusVariant: safeStore.is_active ? 'success' : 'secondary'
    };
  }

  // =============================
  // UTILITY HELPERS
  // =============================

  // Obter slug da loja (fallback para diferentes nomes de campos)
  static getStoreSlug(store) {
    return store.slug || store.store_slug || store.store_name || store.id;
  }

  // Gerar slug a partir do nome da loja
  static generateStoreSlug(storeName) {
    if (!storeName) return '';
    
    return storeName
      .toLowerCase()
      .trim()
      // Remove acentos e caracteres especiais (básico)
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, '') // Remove caracteres especiais
      .replace(/\s+/g, '-') // Substitui espaços por hífens
      .replace(/-+/g, '-') // Remove múltiplos hífens
      .replace(/^-|-$/g, ''); // Remove hífens no início/fim
  }

  // Verificar disponibilidade do nome da loja (completo com slug)
  static async checkStoreNameAvailability(storeName) {
    try {
      if (!storeName || storeName.trim().length < 2) {
        return {
          success: false,
          available: false,
          error: 'Nome da loja deve ter pelo menos 2 caracteres',
          slug: '',
          normalized_slug: '',
          suggestions: []
        };
      }

      const slug = this.generateStoreSlug(storeName);
      
      if (!slug || slug.length < 2) {
        return {
          success: false,
          available: false,
          error: 'Nome da loja deve conter caracteres válidos',
          slug: storeName,
          normalized_slug: slug,
          suggestions: []
        };
      }

      const response = await this.checkStoreSlugAvailability(slug);
      
      return {
        success: true,
        available: response.available || false,
        error: response.error || null,
        slug: storeName,
        normalized_slug: response.normalized_slug || slug,
        exists: response.exists || false,
        suggestions: response.suggestions || []
      };

    } catch (error) {
      console.error('Erro ao verificar disponibilidade do nome da loja:', error);
      
      return {
        success: false,
        available: false,
        error: 'Erro ao verificar disponibilidade. Tente novamente.',
        slug: storeName,
        normalized_slug: this.generateStoreSlug(storeName),
        suggestions: []
      };
    }
  }

  // Verificar se um erro 404 indica ausência de lojas (não é um erro real)
  static isNoStoresFound(error) {
    if (error.response?.status !== 404) {
      return false;
    }
    
    const url = error.config?.url || '';
    // Se a URL contém my_seller_data ou é a listagem geral, então é apenas "sem lojas"
    return url.includes('my_seller_data') || 
           (url.includes('seller-stores') && !url.match(/\/[^\/]+\/$/));
  }

  // Verificar se usuário tem permissão para criar/gerenciar loja
  static hasPermission(userPlan) {
    return [2, 5, 9, 10, 11].includes(userPlan);
  }

  // Obter mensagem de erro amigável
  static getErrorMessage(error) {
    if (error.response?.status === 403) {
      return "Você não tem permissão para acessar este recurso. Apenas planos Pro e Business podem criar lojas.";
    }
    
    if (error.response?.status === 400) {
      const data = error.response.data;
      if (data.non_field_errors) {
        return data.non_field_errors[0];
      }
      
      // Retornar primeiro erro encontrado
      const errorMessages = Object.values(data).flat();
      return errorMessages[0] || "Dados inválidos";
    }

    if (error.response?.status === 404) {
      // Usar helper para verificar se é "sem lojas" vs "loja não encontrada"
      if (SellerStoreServices.isNoStoresFound(error)) {
        return "Você ainda não possui lojas criadas";
      }
      return "Loja não encontrada";
    }

    if (error.response?.status >= 500) {
      return "Erro interno do servidor. Tente novamente mais tarde.";
    }

    return error.message || "Erro desconhecido";
  }

  // Construir parâmetros de busca
  static buildSearchParams(filters = {}) {
    const params = {};
    
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    if (filters.is_active !== undefined) {
      params.is_active = filters.is_active;
    }

    // Paginação
    if (filters.page) {
      params.page = filters.page;
    }
    
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    return params;
  }

  // Normalizar URLs de redes sociais (adicionar https:// se necessário)
  static normalizeSocialUrls(socialLinks) {
    const normalized = {};
    
    Object.keys(socialLinks || {}).forEach(key => {
      const url = socialLinks[key];
      if (url && url.trim()) {
        normalized[key] = url.startsWith('http') ? url : `https://${url}`;
      }
    });
    
    return normalized;
  }

  // Limpar dados vazios do formulário
  static cleanFormData(formData) {
    const cleaned = { ...formData };
    
    // Limpar social_links vazios
    if (cleaned.social_links) {
      Object.keys(cleaned.social_links).forEach(key => {
        if (!cleaned.social_links[key] || !cleaned.social_links[key].trim()) {
          delete cleaned.social_links[key];
        }
      });
    }
    
    // Limpar contact_info vazios
    if (cleaned.contact_info) {
      Object.keys(cleaned.contact_info).forEach(key => {
        if (!cleaned.contact_info[key] || !cleaned.contact_info[key].trim()) {
          delete cleaned.contact_info[key];
        }
      });
    }
    
    // Limpar campos de tráfego vazios
    if (!cleaned.meta_pixel_id || !cleaned.meta_pixel_id.trim()) {
      delete cleaned.meta_pixel_id;
    }
    if (!cleaned.google_analytics_id || !cleaned.google_analytics_id.trim()) {
      delete cleaned.google_analytics_id;
    }
    
    return cleaned;
  }
}

export default SellerStoreServices;